import React, { useRef } from 'react';

import PrivateLayout from "layouts/private";
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import useToken from 'hooks/useToken';
import { useQuery } from 'react-query';
import { Button, Card, CardActions, CardContent, CardHeader, Container, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { fetchTransaction } from 'apiRequest/transaction';
import dateTime from 'utils/datetime';
import { Link as RouterLink } from 'react-router-dom';
import currency from 'utils/currency';
import TransactionReceipt from 'components/TransactionReceipt';
import { Print } from '@mui/icons-material';
import ReactToPrint from 'react-to-print';

export default function TransactionShow() {
  const { id } = useParams();

  const { token } = useToken();
  const receiptComponentRef = useRef();

  const { data: transaction, isLoading, isError, error } = useQuery("server-fetchTransaction", () => {
    return fetchTransaction({
      token, id, params: {
        partials: [
          'subTransactions.soldCapital.account',
          'subTransactions.boughtCapital.account',
          'subTransactions.soldCapital.figure',
          'subTransactions.boughtCapital.figure',
        ]
      }
    });
  });

  return (
    <PrivateLayout loading={isLoading} error={error} isError={isError}>
      <TransactionReceipt payload={transaction?.data.data} ref={receiptComponentRef} />
      {!isEmpty(transaction?.data.data) && (
        <Container maxWidth="lg">
          <Card sx={{ padding: "10px" }}>
            <CardHeader
              title={`${transaction?.data.data.id}`}
              subheader={`${dateTime.format(transaction?.data.data.created_at)}`}
            />
            <Divider />
            <CardContent>
              <Stack direction="column" spacing={1.5}>
                <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                  <CardText
                    label="Bought Amount"
                    value={`${currency.format(transaction?.data.data.total_bought_amount, 2)} ${transaction?.data.data.bought_currency} `}
                  />
                  <CardText
                    label="Sold Amount"
                    value={`${currency.format(transaction?.data.data.total_sold_amount, 2)} ${transaction?.data.data.sold_currency}`}
                  />
                  <CardText
                    label="Exchange Rate"
                    value={`${currency.calculateExchnageRate({
                      boughtCurrency: transaction?.data.data.bought_currency,
                      soldCurrency: transaction?.data.data.sold_currency,
                      boughtAmount: transaction?.data.data.total_bought_amount,
                      soldAmount: transaction?.data.data.total_sold_amount,
                    })}`}
                  />
                </Stack>
                <Divider variant="middle" />
                <CardText label="Debtor" value={transaction?.data.data.debtor} />
                <Divider variant="middle" />
                <CardText label="Description" value={transaction?.data.data.description} />
                <Divider variant="middle" />
                <CardText label="User Id" value={transaction?.data.data.user_id} />
                <Divider variant="middle" />
                <Box>
                  <Typography gutterBottom variant="caption" display="block">
                    Sub Transactions
                  </Typography>
                  <SubTransactionsTable subTransactions={transaction?.data.data.sub_transactions} />
                </Box>
              </Stack>
            </CardContent>
            <CardActions>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  padding: "0 10px"
                }}
              >
                <Button
                  component={RouterLink}
                  to={`/transactions/${transaction?.data.data.id}/update`}
                  color="primary"
                  size="large"
                  variant="outlined"
                >
                  Update
                </Button>
                <ReactToPrint
                  trigger={() => <Button color="primary" size="large" variant="outlined">Print<Print /></Button>}
                  content={() => receiptComponentRef.current}
                />
              </Stack>
            </CardActions>
          </Card>
        </Container>
      )}
    </PrivateLayout >
  );
}

const CardText = ({ label, value }) => {
  return (
    <Box>
      <Typography gutterBottom variant="caption" display="block">
        {label}
      </Typography>
      <Typography variant="body1">
        {value}
      </Typography>
    </Box>
  );
};


const SubTransactionsTable = ({ subTransactions }) => {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Bought Currency</TableCell>
          <TableCell>Sold Currency</TableCell>
          <TableCell>Bought Amount</TableCell>
          <TableCell>Sold Amount</TableCell>
          <TableCell>Bought Account</TableCell>
          <TableCell>Sold Account</TableCell>
          <TableCell>Exchange Rate</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subTransactions.map(({
          id,
          bought_capital,
          sold_capital,
          bought_amount,
          sold_amount,
        }) => (
          <TableRow key={id}>
            <TableCell>{id}</TableCell>
            <TableCell>{bought_capital.figure.code}</TableCell>
            <TableCell>{sold_capital.figure.code}</TableCell>
            <TableCell>{currency.format(bought_amount, 2)}</TableCell>
            <TableCell>{currency.format(sold_amount, 2)}</TableCell>
            <TableCell>{bought_capital.account?.name}</TableCell>
            <TableCell>{sold_capital.account?.name}</TableCell>
            <TableCell>{
              currency.calculateExchnageRate({
                boughtCurrency: bought_capital.figure.currency,
                soldCurrency: sold_capital.figure.currency,
                boughtAmount: bought_amount,
                soldAmount: sold_amount,
              })
            }
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
